import { format, formatDistance, formatRelative, isToday, isYesterday, differenceInDays, differenceInWeeks } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Formats a date using date-fns with Spanish locale
 * @param date - Date string or Date object
 * @param formatStr - Format string (default: 'PP' for long date)
 * @returns Formatted date string in Spanish
 *
 * Common format strings:
 * - 'PP' -> "15 oct 2025"
 * - 'PPP' -> "15 de octubre de 2025"
 * - 'PPPP' -> "miércoles, 15 de octubre de 2025"
 * - 'd MMM yyyy' -> "15 oct 2025"
 */
export function formatDate(date: string | Date, formatStr = 'PP'): string {
  try {
    return format(new Date(date), formatStr, { locale: es });
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(date);
  }
}

/**
 * Formats a date relative to now (e.g., "hace 3 días")
 * @param date - Date string or Date object
 * @returns Relative date string in Spanish
 */
export function formatRelativeDate(date: string | Date): string {
  try {
    return formatDistance(new Date(date), new Date(), {
      locale: es,
      addSuffix: true
    });
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return String(date);
  }
}

/**
 * Formats a date relative to a specific date
 * @param date - Date string or Date object
 * @param baseDate - Base date for comparison
 * @returns Relative date string (e.g., "ayer a las 14:30")
 */
export function formatRelativeToDate(date: string | Date, baseDate: Date = new Date()): string {
  try {
    return formatRelative(new Date(date), baseDate, { locale: es });
  } catch (error) {
    console.error('Error formatting relative to date:', error);
    return String(date);
  }
}

/**
 * Smart date formatter for property listings
 * - "Hoy" if today
 * - "Ayer" if yesterday
 * - "Hace X días" if less than 7 days
 * - "Hace X semanas" if less than 30 days
 * - "15 oct 2025" for older dates
 *
 * @param date - Date string or Date object
 * @returns User-friendly date string in Spanish
 */
export function formatPropertyDate(date: string | Date): string {
  try {
    const dateObj = new Date(date);
    const now = new Date();

    // Check if invalid date
    if (isNaN(dateObj.getTime())) {
      return 'Fecha no disponible';
    }

    // Today
    if (isToday(dateObj)) {
      return 'Hoy';
    }

    // Yesterday
    if (isYesterday(dateObj)) {
      return 'Ayer';
    }

    // Less than 7 days
    const diffDays = differenceInDays(now, dateObj);
    if (diffDays > 0 && diffDays < 7) {
      return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
    }

    // Less than 30 days (show in weeks)
    if (diffDays >= 7 && diffDays < 30) {
      const weeks = differenceInWeeks(now, dateObj);
      return `Hace ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`;
    }

    // Older than 30 days - show formatted date
    return format(dateObj, 'd MMM yyyy', { locale: es });
  } catch (error) {
    console.error('Error formatting property date:', error);
    return 'Fecha no disponible';
  }
}

/**
 * Formats a date for blog posts
 * - "Hace X minutos/horas" if less than 24 hours
 * - "Ayer" if yesterday
 * - "Hace X días" if less than 7 days
 * - "15 de octubre de 2025" for older dates
 *
 * @param date - Date string or Date object
 * @returns User-friendly date string for blog posts
 */
export function formatBlogDate(date: string | Date): string {
  try {
    const dateObj = new Date(date);
    const now = new Date();

    // Check if invalid date
    if (isNaN(dateObj.getTime())) {
      return 'Fecha no disponible';
    }

    const diffDays = differenceInDays(now, dateObj);

    // Less than 24 hours - show "hace X horas"
    if (diffDays === 0) {
      return formatDistance(dateObj, now, { locale: es, addSuffix: true });
    }

    // Yesterday
    if (isYesterday(dateObj)) {
      return 'Ayer';
    }

    // Less than 7 days
    if (diffDays > 0 && diffDays < 7) {
      return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
    }

    // Older - full date
    return format(dateObj, 'd \'de\' MMMM \'de\' yyyy', { locale: es });
  } catch (error) {
    console.error('Error formatting blog date:', error);
    return 'Fecha no disponible';
  }
}

/**
 * Formats a date with time (e.g., "15 oct 2025, 14:30")
 * @param date - Date string or Date object
 * @returns Formatted date with time in Spanish
 */
export function formatDateTime(date: string | Date): string {
  try {
    return format(new Date(date), "d MMM yyyy, HH:mm", { locale: es });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return String(date);
  }
}

/**
 * Formats a date for "last updated" displays
 * @param date - Date string or Date object
 * @returns Formatted string like "Última actualización: 15 oct 2025"
 */
export function formatLastUpdated(date: string | Date): string {
  try {
    const formattedDate = format(new Date(date), "d MMM yyyy", { locale: es });
    return `Última actualización: ${formattedDate}`;
  } catch (error) {
    console.error('Error formatting last updated:', error);
    return 'Última actualización: no disponible';
  }
}

/**
 * Formats a month name in Spanish
 * @param monthNumber - Month number (1-12)
 * @returns Month name in Spanish
 */
export function getMonthName(monthNumber: number): string {
  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  if (monthNumber < 1 || monthNumber > 12) {
    return 'Mes inválido';
  }

  return months[monthNumber - 1];
}

/**
 * Formats a date range for filters
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted range like "Desde 1 enero 2025 hasta 31 diciembre 2025"
 */
export function formatDateRange(startDate: string | Date, endDate: string | Date): string {
  try {
    const start = format(new Date(startDate), "d 'de' MMMM 'de' yyyy", { locale: es });
    const end = format(new Date(endDate), "d 'de' MMMM 'de' yyyy", { locale: es });
    return `Desde ${start} hasta ${end}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Rango de fechas no disponible';
  }
}
